'use client';

import { FunctionComponent, useState } from 'react';
import { useBrandLocaleValue } from '@/react/hooks/useBrandLocaleValue';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import { Drawer } from '@/react/components/core-ui/Drawer';
import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import { useLocale } from '@/react/hooks/useLocale';
import {
  useDrawer,
  useDrawerActions
} from '@/react/components/core-ui/Drawer/hooks';
import useConditionallyDisableScrolling from '@/react/components/core-ui/hooks/useConditionallyDisableScrolling';
import type { INavigation } from '@/services/models/Navigation/INavigation';
import { NavigationVM } from '@/react/view-models/NavigationVM';
import { EventType } from '@/services/isomorphic/UserInteractionService';
import { Link } from '@/react/components/core-ui/Link';
import { DataModelTarget } from '@/services/isomorphic/integrations/BuilderService';
import { ContentType } from '@/services/isomorphic/ContentService';
import { FetchContentWhenActive } from '@/react/components/content/core/FetchContentWhenActive';
import { withEcommerceEnabled } from '@/react/components/utility/EcommerceEnabled';
import { MobileUtilityLinks } from './components/MobileUtilityLinks';
import { MobileNavItem } from './components/MobileNavItem';
import { NavigationLink } from './components/NavigationLink';
import { AccordionNavigationLink } from './components/AccordionNavigationLink';
import { NavigationBack } from './components/NavigationBack';
import { MobileDrawer } from './components/MobileDrawer';

import S from './styles.base.module.scss';
import { header_navigation_open } from "@/lang/__generated__/ahnu/header_navigation_open";
import { header_navigation_close } from "@/lang/__generated__/ahnu/header_navigation_close";

/**
 * Formats a navigation link name for GA tagging.
 * Produces lowercase hyphenated format (e.g. "women").
 * @param name - The name of the navigation link.
 * @returns The formatted navigation link name.
 */
function formatNavLinkName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

interface IMobileProps {
  /**
   * The navigation DTO's to initialize view model.
   */
  navigationItems: ReadonlyArray<INavigation>;
}

/**
 * The Mobile navbar component that acts as a drawer.
 */
export const Mobile: FunctionComponent<IMobileProps> = withEcommerceEnabled(
  function Mobile({ navigationItems }) {
    const [msg] = useLocaleMessage();
    const { language, country } = useLocale();
    const { isOpen, closeDrawer, openDrawer } = useDrawer();
    useConditionallyDisableScrolling(isOpen);
    const { closeAllDrawers } = useDrawerActions();
    const [navigationVM] = useState(() => new NavigationVM(navigationItems));
    const menuConfig = ConfigurationService.getConfig(
      'menu',
      language,
      country
    );
    const showUtilityLinks = menuConfig.getSetting(
      'mainMenu.showUtilityLinks'
    ).value;

    const sitBelowHeader = menuConfig.getSetting(
      'mobileMenu.sitBelowHeader'
    ).value;

    const MenuIconType = useBrandLocaleValue<IconTypes>(
      () => ({
        default: IconTypes.Bars,
        AHNU: IconTypes.BarsLight
      }),
      []
    );

    const showBackButtonOnParentDrawer = menuConfig.getSetting(
      'mobileMenu.showBackButtonOnParentDrawer'
    ).value;

    const showCloseButton = menuConfig.getSetting(
      'mobileMenu.showCloseButton'
    ).value;

    const isAccordionNavigation = menuConfig.getSetting(
      'mobileMenu.isAccordionNavigation'
    ).value;

    const showBreadcrumbLabel = menuConfig.getSetting(
      'mobileMenu.showBreadcrumbLabel'
    ).value;

    const showUtilityLinksOnPage2 = menuConfig.getSetting(
      'mobileMenu.showUtilityLinksOnPage2'
    ).value;

    return (
      <>
        <button
          className={S.buttonWithIcon}
          onClick={isOpen ? closeDrawer : openDrawer}
          type="button"
          title={msg(header_navigation_open)}
          aria-label={msg(header_navigation_open)}
        >
          <Icon
            icon={MenuIconType}
            className={S.drawerIcon}
            size={IconSizeProp.SizeLG}
          />
        </button>

        <Drawer
          open={isOpen}
          fromLeft
          rootClassName={S.parentDrawerRoot}
          drawerClassName={S.parentDrawer}
          showCurtain
          usePortal={sitBelowHeader}
        >
          {showBackButtonOnParentDrawer && (
            <NavigationBack onClick={closeDrawer} />
          )}
          {showCloseButton && (
            <div className={S.closeButtonRow}>
              <button
                type="button"
                className={S.closeButton}
                onClick={closeDrawer}
                aria-label={msg(header_navigation_close)}
              >
                <Icon icon={IconTypes.Close} size={IconSizeProp.SizeLG} />
              </button>
            </div>
          )}
          <ul className={S.nav}>
            {navigationVM.navigationModels.map((item) => {
              if (!item.categories || item.categories.length === 0) {
                return (
                  <MobileNavItem key={item.id}>
                    <Link
                      className={S.navLink}
                      href={`${item.href}`}
                      onClick={closeAllDrawers}
                      interactionDetails={{
                        action: EventType.NavigationLink,
                        link: {
                          linkName: formatNavLinkName(item.name),
                          linkCategory: 'top-navigation',
                          linkSection: formatNavLinkName(item.name)
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  </MobileNavItem>
                );
              }

              const NavLinkComponent = isAccordionNavigation
                ? AccordionNavigationLink
                : NavigationLink;

              return (
                <MobileDrawer
                  key={item.id}
                  label={item.name}
                  backLabel={showBreadcrumbLabel ? item.name : undefined}
                >
                  <div className={S.categoriesSection}>
                    <MobileNavItem>
                      <NavLinkComponent item={item} />
                    </MobileNavItem>
                  </div>
                  <div className={S.contentSection}>
                    <FetchContentWhenActive
                      options={{
                        id: DataModelTarget.NavigationContentModel,
                        tag: `${item.id}-navigation-content`,
                        type: ContentType.Model
                      }}
                      isActive={isOpen}
                      handleLinkClick={closeAllDrawers}
                    />
                  </div>
                  {showUtilityLinks && showUtilityLinksOnPage2 && (
                    <div className={S.utilityLinksSection}>
                      <MobileUtilityLinks />
                    </div>
                  )}
                </MobileDrawer>
              );
            })}
          </ul>

          {showUtilityLinks && (
            <div className={S.utilityLinksSection}>
              <MobileUtilityLinks />
            </div>
          )}
        </Drawer>
      </>
    );
  }
);
